import React, { useState, useMemo } from 'react';
import { useApi } from '../hooks/useApi';
import { api } from '../api';
import { fmt } from '../utils';
import { Card, CardHeader, StatCard, StatGrid, Badge, SearchInput, Skeleton, PageWrap, PageHeader, Table, useIsMobile } from '../components/UI';
import '../styles/TrialBalance.css';

export default function TrialBalance({ sourceId, syncRunId }) {
  const { data, loading } = useApi(() => api.trialBalance(sourceId, syncRunId), [sourceId, syncRunId]);
  const [search, setSearch] = useState('');
  const isMobile = useIsMobile();

  const rows = useMemo(() => {
    let r = (data?.ledgers || []).filter(x => Math.abs(parseFloat(x.balance ?? 0)) > 0.01);
    if (search) { const q = search.toLowerCase(); r = r.filter(x => (x.ledger || x.raw_ledger_name || '').toLowerCase().includes(q)); }
    return r;
  }, [data, search]);

  return (
    <PageWrap>
      <PageHeader title="Trial Balance" />

      {data && data.balanced === false && (
        <div className="tb-imbalance-callout">
          <div className="tb-imbalance-title">
            ⚠️ Trial balance imbalance detected
          </div>
          <div className="tb-imbalance-text">
            Difference: ₹{fmt(Math.abs(data.netTotal ?? ((data.creditTotal ?? 0) - (data.debitTotal ?? 0))))}
            {' '}({((data.creditTotal ?? 0) - (data.debitTotal ?? 0)) > 0 ? 'Cr exceeds Dr' : 'Dr exceeds Cr'})
          </div>
          <div className="tb-imbalance-note">
            This reflects actual posted ledger balances. Tally's UI may display the books as
            balanced via grouping logic or "Difference in Opening Balances" plug entries.
            Investigate provisional accruals (Bills to Come/Make), contra entries, and
            opening balance setup.
          </div>
        </div>
      )}

      <StatGrid cols={isMobile ? 2 : 4}>
        <StatCard label="Balanced"     value={data?.balanced == null ? '—' : data.balanced ? 'YES' : 'NO'} accent={data?.balanced ? 'green' : 'red'} />
        <StatCard label="Dr Total"     value={data ? `₹${fmt(data.debitTotal ?? data.grandTotal)}` : '—'} accent="red" />
        <StatCard label="Cr Total"     value={data ? `₹${fmt(data.creditTotal ?? data.grandTotal)}` : '—'} accent="green" />
        <StatCard label="Ledger Count" value={data?.ledgers?.length ?? '—'} />
      </StatGrid>

      <Card>
        <CardHeader
          title="Ledger Balances"
          right={<SearchInput value={search} onChange={setSearch} placeholder="Search ledger..." />}
        />
        {loading ? <Skeleton rows={8} /> : (
          <Table
            columns={[
              { label: 'Ledger', key: 'ledger',
                render: r => <span>{r.ledger ?? r.raw_ledger_name ?? '—'}</span> },
              { label: 'Balance (₹)', key: 'balance', right: true, mono: true,
                render: r => {
                  const b = parseFloat(r.balance ?? 0);
                  const side = r.side ?? (b < 0 ? 'Dr' : 'Cr');
                  return (
                    <span style={{ color: side === 'Dr' ? 'var(--red)' : 'var(--green)' }}>
                      {fmt(Math.abs(b))}
                    </span>
                  );
                }},
              { label: 'Side', key: 'side',
                render: r => {
                  const b = parseFloat(r.balance ?? 0);
                  const side = r.side ?? (b < 0 ? 'Dr' : 'Cr');
                  return <Badge text={side} variant={side === 'Dr' ? 'red' : 'green'} />;
                }},
            ]}
            rows={rows}
            emptyMsg="No data — run a sync first"
          />
        )}
      </Card>
    </PageWrap>
  );
}
